import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { concat, of, from, EMPTY, Subscription, Subject, distinctUntilChanged, forkJoin } from 'rxjs';
import { map, tap, switchMap, concatMap, filter, finalize, debounceTime, catchError } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
// import * as FileSaver from 'file-saver';
import { formatDate } from '@angular/common';
import stringInject from 'stringinject';
import { saveAs } from 'file-saver';

import { environment } from 'src/environments/environment';
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'
import { Product, ProductParts } from 'src/app/shared/product';
import { Document, DocItems, DocumentExport } from 'src/app/shared/document';
import { PrintReportSetting } from 'src/app/shared/global-settings-data';
import { DocTypes } from 'src/app/shared/doc-list-entry';
import { Currency } from 'src/app/shared/currency';
import { PerListEntry } from 'src/app/shared/per-list-entry';
import { DocumentApiService } from 'src/app/services/document-api.service';
import { ClientApiService } from 'src/app/services/client-api.service';
import { ProductApiService } from 'src/app/services/product-api.service';
import { RestApiService } from 'src/app/services/rest-api.service';
import { ReportApiService } from 'src/app/services/report-api.service';
import { CliListEntry } from 'src/app/shared/cli-list-entry';

/**
 * Internal options object passed through the export pipeline stages.
 * Carries the source document and all user-selected export preferences
 * as immutable context so each `concatMap` stage can both read inputs
 * and forward the same object to the next stage without mutation.
 */
interface BuildOptions {
  /** The source document to be exported. */
  opDocument: Document;
  /** Document-type-specific overrides merged into the export payload. */
  opDocumentSpecific: DocumentExport;
  /** When `true`, items with zero quantity are included in the export. */
  opCountEmpty: boolean;
  /** When `true`, currency columns are rendered in the print form. */
  opShowCurrency: boolean;
  /** When `true`, discount columns are rendered in the print form. */
  opShowDiscount: boolean;
  /** When `true`, individual item details are shown in the print form. */
  opShowItem: boolean;
  /** When `true`, the full item table section is shown in the print form. */
  opShowItemTable: boolean;
}

/**
 * Dialog component that assembles a structured {@link DocumentExport} payload
 * and submits it to a jsreport server to generate a printable DOCX document.
 *
 * ### Pipeline overview
 * When {@link onExport} is called, a `BuildOptions` value is pushed onto the
 * {@link buildExportData} Subject. The constructor sets up a single long-lived
 * RxJS pipeline subscribed to that Subject which sequentially:
 * 1. Initialises default labels and field values from the source document.
 * 2. Resolves the main and document currency names via the REST API.
 * 3. Converts the document total to words via the REST API.
 * 4. Fetches seller and buyer company data from the client API.
 * 5. Fetches seller, buyer and owner person data from the client API.
 * 6. Fetches the document-type metadata from the document API.
 * 7. Maps document line items into export-friendly objects.
 * 8. Enriches composite products with their part lists.
 *
 * Each stage uses `catchError` so an individual API failure does not
 * terminate the Subject; the pipeline continues with the data collected
 * so far.
 *
 * ### Authentication notes
 * @see {@link https://jsreport.net/learn/authentication}
 * @see {@link https://jsreport.net/blog/single-sign-on-with-keycloak}
 *
 * Working SSO configuration as of 2024/01/02:
 * 1. `asset/app.config.json` must point to the same Keycloak server as the
 *    WebAPI `keycloak.json` and the jsreport server configuration.
 * 2. Every jsreport server config file must contain the correct `client.id`
 *    and client secret.
 * 3. Users must exist in both Keycloak and the jsreport user store, or
 *    group mapping must be configured.
 */
@Component({
  selector: 'app-export-document',
  templateUrl: './export-document.component.html',
  styleUrls: ['./export-document.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class ExportDocumentComponent implements OnInit, OnChanges, OnDestroy {

  /** The fully assembled export payload sent to the report server. Populated by the pipeline. */
  documentExport: DocumentExport = {};

  /**
   * Document-type-specific field overrides injected by the parent component.
   * Values provided here are merged (spread) over the defaults assembled by
   * the pipeline, allowing callers to customise labels or pre-fill specific
   * sections without modifying the core logic.
   */
  @Input() documentSpecific: DocumentExport;

  /** List of print-form templates available for the current document type. */
  reportList: PrintReportSetting[] = [];

  /** The print-form template currently selected by the user. */
  selectedReport: PrintReportSetting = {} as PrintReportSetting;

  /**
   * Returns `true` when the user may proceed to print.
   * The condition is satisfied either when no print forms are available
   * (fallback template will be used) or when the user has selected a form.
   */
  /** True when the user may proceed to print — either no print forms exist (fallback used) or one is selected. */
  public get printFormSelected(): boolean { return (((this.reportList || []).length < 1) || (this.selectedReport ? true : false)); }

  /**
   * Static stub payload used during development and manual testing.
   * Represents a sample invoice document so the template can be rendered
   * without a live database connection.
   */
  documentExportStub = {
    "DocumentId": 90089
    , "Owner": {}
    , "Header": {
      "Date": "2021-03-21T06:00:00.000Z",
      "DocumentName": "Договор на поставку №ДП-23-11118 от 21.03.2021 (OLTEST)",
      "DocumentNumber": "ДП-23-11118",
      "ValidTo": "2021-03-26T10:00:00.000Z",
      "ExpiredOn": "2021-04-23T18:21:23.000Z"
    }
    , "Seller": {}
    , "Buyer": {}
    , "Summary": { "TotalByWords": "Сто вісімдесят чотири тисячі двісті двадцять гривень 71 копійка" }
  };

  /** Active subscription for loading a document by its numeric ID. */
  private _documentQuery: Subscription;

  /**
   * Active subscription for the export pipeline.
   * Assigned once in the constructor from {@link buildExportData}.
   * Also held by {@link dumbProcedure} (legacy implementation).
   */
  private _exportQuery: Subscription;

  /** Active subscription for loading the available print-form list in {@link onShowDialog}. */
  private reportListQuery: Subscription;

  /** Temporary storage for seller company data, used by the image-loading stage. */
  private _sellerCompanyData: CliListEntry | null = null;

  /** Temporary storage for seller person data, used by the image-loading stage. */
  private _sellerPersonData: PerListEntry | null = null;

  /**
   * Entry point for the export pipeline.
   * Call {@link onExport} to push a new {@link BuildOptions} value here.
   * The constructor subscribes a long-lived `concatMap` chain to this Subject.
   * Because every inner observable uses `catchError`, the Subject itself
   * survives individual API failures and remains active for subsequent exports.
   */
  buildExportData: Subject<BuildOptions> = new Subject<BuildOptions>();

  /** Controls whether the dialog is displayed as a modal overlay. */
  modal: boolean = true;

  /** Indicates that an asynchronous operation is in progress. */
  loading: boolean = false;

  /**
   * When `true` the Confirm button is disabled because the export pipeline
   * has not yet completed building the payload.
   */
  notReady: boolean = true;

  /** Status text displayed in the progress bar header during pipeline execution. */
  exportHeader: string = "Експорт документа для друку";

  /*
   * Proforma options
   */

  /** When `true`, discount columns are included in the rendered print form. */
  showDiscount: boolean = false;

  /** When `true`, currency value columns are included in the rendered print form. */
  showCurrency: boolean = false;

  /** When `true`, individual item detail rows are shown in the rendered print form. */
  showItem: boolean = false;

  /** When `true`, the full item table block is visible in the rendered print form. */
  showItemTable: boolean = false;

  /*
   * Invoice options
   */

  /** When `true`, the company logo image is included in the rendered invoice. */
  showLogo: boolean = true;

  /** When `true`, the company stamp image is included in the rendered invoice. */
  showStamp: boolean = true;

  /** When `true`, the authorised-person signature image is included in the rendered invoice. */
  showSign: boolean = true;

  /**
   * When `true`, the development/test print form template is used instead of the
   * production template stored on the report server.
   */
  useDevForm: boolean = false;

  /** Current progress value forwarded to the `p-progressBar` component in the template. */
  value: number = 0;

  /**
   * Title text displayed in the dialog header.
   * @default "Экспорт документа для печати"
   */
  @Input() header: string = "Экспорт документа для печати";

  /**
   * Numeric ID of the document to export.
   * When this changes, {@link loadDocumentById} is called automatically via
   * {@link ngOnChanges} to fetch the full document from the API.
   */
  @Input() documentId: number = 0;

  /**
   * Pre-loaded document object.
   * When provided by the parent, {@link loadDocumentById} is skipped and the
   * pipeline uses this value directly.
   */
  @Input() document: Document = null;

  /**
   * Controls dialog visibility.
   * Two-way bound via {@link displayChange}; set to `false` internally on
   * hide to keep the parent binding in sync.
   */
  @Input() display: boolean = false;

  /**
   * When `true`, document line items with a zero quantity (`DOCITEMQTY = 0`)
   * are included in the export payload and rendered in the print form.
   * @default false
   */
  @Input() countEmpty: boolean = false;

  /**
   * Emits `true` when the user clicks the Confirm button to finalise the export.
   * The parent component should listen to this event to react to successful
   * form generation.
   */
  @Output() done: EventEmitter<boolean> = new EventEmitter();

  /**
   * Emits the new value of {@link display} whenever the dialog is hidden,
   * enabling two-way binding with `[(display)]` in the parent template.
   */
  @Output() displayChange: EventEmitter<boolean> = new EventEmitter();

  /**
   * Injects all required services and sets up the long-lived export pipeline.
   *
   * The pipeline subscribes to {@link buildExportData} and, for each emitted
   * {@link BuildOptions} value, sequentially fetches currency info, the total
   * amount in words, seller/buyer/owner company and person data, document-type
   * metadata, line items and composite product parts — assembling
   * {@link documentExport} in place.  Each `concatMap` stage uses `catchError`
   * to return `of(options)` on failure so the pipeline continues even when
   * individual API calls fail.
   *
   * @param restapi       - General REST helper for currency and number-to-words calls.
   * @param docApi        - Document API for loading document data and metadata.
   * @param prodApi       - Product API for fetching composite product part lists.
   * @param reportApi     - Report API for submitting the assembled payload to jsreport.
   * @param cliApi        - Client API for resolving company and person records.
   * @param messageService - PrimeNG toast service for user-visible error notifications.
   * @param globalsettingsservice - Application settings provider for currency IDs and report server URLs.
   */
  constructor(
    private restapi: RestApiService,
    private docApi: DocumentApiService,
    private prodApi: ProductApiService,
    private reportApi: ReportApiService,
    private cliApi: ClientApiService,
    private messageService: MessageService,
    private globalsettingsservice: GetglobalsettingsService,
  ) {
    // ─── Single flat pipeline — no nested subscriptions ───────────────────────
    // switchMap cancels any in-flight export when a new buildExportData value
    // arrives (e.g. user toggles an option while pipeline is running).
    // All per-emission work lives inside one of(options).pipe(...) chain so
    // a catchError at the end can swallow failures without killing the Subject.
    // ──────────────────────────────────────────────────────────────────────────
    this._exportQuery = this.buildExportData.pipe(

      debounceTime(150),
      distinctUntilChanged(),
      filter(options => options.opDocument != null),

      tap(() => {
        this.notReady = true;
        this.value = 0;
      }),

      switchMap(options => of(options).pipe(

        // ── Stage 0: initialise documentExport with ALL labels & defaults ──────
        map(options => {
          this.value = 5;
          this.exportHeader = 'Експорт -> ініціалізація';

          this.documentExport = {
            DocumentId: options.opDocument.DOCID,

            Owner: {
              FullNameLabel: 'Відповідальний',
              EmailLabel: 'Електронна пошта',
              PhoneLabel: 'Контактний телефон',
              Email: '',
              FullName: '',
              Phone: '',
              ...options.opDocumentSpecific?.Owner,
            },

            Header: {
              DateLabel: 'Дата',
              DocumentNameLabel: 'Назва',
              DocumentNumberLabel: 'Номер',
              ValidToLabel: 'Дійсний до',
              ExpiredOnLabel: 'Дата виконання',
              CurrencyNameLabel: 'Валюта документа',
              CurrencyRateLabel: 'Курс валюти',
              MainCurrencyNameLabel: 'Основна валюта',
              Date: options.opDocument.DOCDATE,
              DocumentName: options.opDocument.DOCNAMESTR,
              DocumentNumber: options.opDocument.DOCNUMBEREX,
              ValidTo: options.opDocument.DOCVALDATE,
              ExpiredOn: options.opDocument.DOCEXPDATE,
              DateStr: formatDate(options.opDocument.DOCDATE, 'dd.MM.yyyy', 'uk'),
              ValidToStr: options.opDocument.DOCVALDATE
                ? formatDate(options.opDocument.DOCVALDATE, 'dd.MM.yyyy', 'uk')
                : '',
              ExpiredOnStr: options.opDocument.DOCEXPDATE
                ? formatDate(options.opDocument.DOCEXPDATE, 'dd.MM.yyyy', 'uk')
                : '',
              CurrencyName: options.opDocument.DOCCURNAME,
              CurrencyRate: options.opDocument.DOCRATEVAL,
              MainCurrencyName: '',
              ...options.opDocumentSpecific?.Header,
            },

            Seller: {
              CompanyNameLabel: 'ПРОДАВЕЦЬ',
              CompanyIdLabel: 'Код ЄДРПОУ',
              VATIDLabel: 'Код платника ПДВ',
              VATIDDocLabel: 'Свідоцтво ПДВ',
              AccountLabel: 'Рахунок',
              BankLabel: 'Банк',
              AddressLabel: 'Адреса',
              FullNameLabel: 'Відповідальний',
              EmailLabel: 'Електронна пошта',
              PhoneLabel: 'Телефон',
              CLIID: options.opDocument.DOCLCOCLIID,
              PERID: options.opDocument.DOCLCOPERID,
              SignImage: '',
              StampImage: '',
              LogoImage: '',
              CompanyName: '',
              CompanyId: '',
              VATID: '',
              Account: '',
              Bank: '',
              Address: '',
              FullName: '',
              Email: '',
              Phone: '',
              ...options.opDocumentSpecific?.Seller,
            },

            Buyer: {
              CompanyNameLabel: 'ПОКУПЕЦЬ',
              CompanyIdLabel: 'Код ЄДРПОУ',
              VATIDLabel: 'Код платника ПДВ',
              VATIDDocLabel: 'Свідоцтво ПДВ',
              AccountLabel: 'Рахунок',
              BankLabel: 'Банк',
              AddressLabel: 'Адреса',
              FullNameLabel: 'Відповідальний',
              EmailLabel: 'Електронна пошта',
              PhoneLabel: 'Телефон',
              CompanyName: '',
              CompanyId: '',
              VATID: '',
              Account: '',
              Bank: '',
              Address: '',
              FullName: '',
              Email: '',
              Phone: '',
              ...options.opDocumentSpecific?.Buyer,
            },

            ItemsLabel: {
              ItemCodeLabel: 'Код товару',
              ItemNameLabel: 'Найменування товару',
              ItemTariffCodeLabel: 'Код УКТЗЕД',
              ItemNoteLabel: 'Примітки',
              ItemPriceLabel: 'Ціна з ПДВ',
              ItemVPriceLabel: 'Ціна в валюті',
              ItemSumWoVATLabel: 'Сума без ПДВ',
              ItemPriceWoVATLabel: 'Ціна без ПДВ',
              ItemRetailPriceLabel: 'Роздріб',
              ItemRetailVPriceLabel: 'Роздріб в валюті',
              ItemDiscountLabel: 'Знижка',
              ItemQntyLabel: 'Кількість',
              ItemSumLabel: 'Сума з ПДВ',
              ItemVSumLabel: 'Сума в валюті',
              ItemVATLabel: 'ПДВ',
              ItemVendorPriceLabel: 'Ціна постачальника',
              ItemInvoicePriceLabel: 'Ціна по накладній',
              ItemInvoiceSumLabel: 'Сума по накладній',
              ItemIncomePriceLabel: 'Прибуткова ціна',
              ItemIncomeSumLabel: 'Прибуткова сума',
              ItemProdWeightLabel: 'Вага одиниці',
              ItemProdWeightSumLabel: 'Загальна вага',
              ItemContentLabel: {
                ContentHeader: 'Склад: ',
                ItemStrLabel: 'Частина',
                ItemCodeLabel: 'Код',
                ItemNameLabel: 'Назва',
                ItemQntyLabel: 'Кількість',
                ItemPriceLabel: 'Ціна',
                ItemVPriceLabel: 'Ціна у валюті',
              },
              ...options.opDocumentSpecific?.ItemsLabel,
            },

            Items: [],

            Conditions: {
              DeliveryConditionsLabel: 'Умови постачання',
              DeliveryTermsLabel: 'Очікуванний термін виконання (днів)',
              DocumentNoteLabel: 'Примітки документа',
              DeliveryConditions: options.opDocument.DOCDLVBASE,
              DeliveryTerms: options.opDocument.DOCDLVTERM,
              DocumentNote: options.opDocument.DOCNOTE2,
              ...options.opDocumentSpecific?.Conditions,
            },

            Summary: {
              TotalSumLabel: 'Загалом, з ПДВ',
              TotalVSumLabel: 'Загалом, з ПДВ, в валюті',
              TotalSumWithOutVATLabel: 'Сума без ПДВ',
              TotalSumVATLabel: 'Сума ПДВ',
              TotalItemsLabel: 'Усього найменувань',
              TotalQuantityLabel: 'Загальна кількість',
              TotalByWordsLabel: 'Усього прописом',
              TotalSum: options.opDocument.DOCSUM,
              TotalVSum: options.opDocument.DOCVSUM,
              TotalSumWithOutVAT: options.opDocument.DOCSUMWOVAT,
              TotalSumVAT: options.opDocument.DOCSUM - options.opDocument.DOCSUMWOVAT,
              TotalItems: options.opDocument.DOCITEMS,
              TotalQuantity: options.opDocument.DOCTOTALITEMS,
              TotalByWords: '',
              ...options.opDocumentSpecific?.Summary,
            },
          };

          return options;
        }),

        // ── Stage 1: main currency name ─────────────────────────────────────────
        concatMap(options => this.restapi.getCurrencyInfoByID(this.globalsettingsservice.getMainCurrencyId()).pipe(
          map(data => {
            if (data && data.length > 0) {
              this.documentExport.Header.MainCurrencyName = data[0].CURNAME;
            }
            return options;
          }),
          catchError(err => {
            environment.production || console.error('*** ExportDocumentComponent getCurrencyInfoByID (main currency) error: ', err);
            return of(options);
          }),
          finalize(() => {
            this.value += 10;
            this.exportHeader = 'Експорт -> основна валюта';
          })
        )),

        // ── Stage 2: document currency name ────────────────────────────────────
        concatMap(options => this.restapi.getCurrencyInfoByID(options.opDocument.DOCCURRID).pipe(
          map(data => {
            if (data && data.length > 0) {
              this.documentExport.Header.CurrencyName = data[0].CURNAME;
            }
            return options;
          }),
          catchError(err => {
            environment.production || console.error('*** ExportDocumentComponent getCurrencyInfoByID (document currency) error: ', err);
            return of(options);
          }),
          finalize(() => {
            this.value += 10;
            this.exportHeader = 'Експорт -> валюта документа';
          })
        )),

        // ── Stage 3: total amount in words ──────────────────────────────────────
        concatMap(options => this.restapi.getNumberByWords(options.opDocument.DOCSUM).pipe(
          map(data => {
            if (data && data.result) {
              this.documentExport.Summary.TotalByWords =
                data.result.convertedInteger + ' ' +
                data.result.integerCurrency + ' ' +
                data.result.fractional + ' ' +
                data.result.fractionalCurrency;
            }
            return options;
          }),
          catchError(err => {
            environment.production || console.error('*** ExportDocumentComponent getNumberByWords error: ', err);
            return of(options);
          }),
          finalize(() => {
            this.value += 10;
            this.exportHeader = 'Експорт -> сума прописом';
          })
        )),

        // ── Stage 4: seller company ─────────────────────────────────────────────
        concatMap(options => this.cliApi.getCliListEntry(options.opDocument.DOCLCOCLIID).pipe(
          map(data => {
            if (data && data.length > 0) {
              const clr = data[0];
              this._sellerCompanyData = clr;
              this.documentExport.Seller = {
                ...this.documentExport.Seller,
                CompanyName: clr.CLIENTSNAME,
                CompanyId: clr.CLIENTREGCODE || '',
                VATID: clr.CLIENTVATREGCODE || '',
                VATIDDoc: clr.CLIENTVATREGNUM || '',
                Account: clr.CLIENTACCOUNT || '',
                Bank: clr.CLIENTBANK || '',
                Account2: clr.CLIENTBANK2ACC || '',
                Address:
                  (clr.CLIENTZIP ? clr.CLIENTZIP + ', ' : '') +
                  (clr.CLIENTCOUNTRY ? clr.CLIENTCOUNTRY + ', ' : '') +
                  (clr.CLIENTCITY ? clr.CLIENTCITY + ', ' : '') +
                  (clr.CLIENTADDR1 ? clr.CLIENTADDR1 + ', ' : '') +
                  (clr.CLIENTADR2 ? clr.CLIENTADR2 + ', ' : ''),
              };
            }
            return options;
          }),
          catchError(err => {
            environment.production || console.error('*** ExportDocumentComponent getCliListEntry (Seller company) error: ', err);
            return of(options);
          }),
          finalize(() => {
            this.value += 10;
            this.exportHeader = 'Експорт -> дані компанії продавця';
          })
        )),

        // ── Stage 5: buyer company ──────────────────────────────────────────────
        concatMap(options => this.cliApi.getCliListEntry(options.opDocument.DOCPCOCLIID).pipe(
          map(data => {
            if (data && data.length > 0) {
              const clr = data[0];
              this.documentExport.Buyer = {
                ...this.documentExport.Buyer,
                CompanyName: clr.CLIENTSNAME,
                CompanyId: clr.CLIENTREGCODE || '',
                VATID: clr.CLIENTVATREGCODE || '',
                VATIDDoc: clr.CLIENTVATREGNUM || '',
                Account: clr.CLIENTBANK2ACC || '',
                Account2: clr.CLIENTACCOUNT || '',
                Bank: clr.CLIENTBANK || '',
                Address:
                  (clr.CLIENTZIP ? clr.CLIENTZIP + ', ' : '') +
                  (clr.CLIENTCOUNTRY ? clr.CLIENTCOUNTRY + ', ' : '') +
                  (clr.CLIENTCITY ? clr.CLIENTCITY + ', ' : '') +
                  (clr.CLIENTADDR1 ? clr.CLIENTADDR1 + ', ' : '') +
                  (clr.CLIENTADR2 ? clr.CLIENTADR2 + ', ' : ''),
              };
            }
            return options;
          }),
          catchError(err => {
            environment.production || console.error('*** ExportDocumentComponent getCliListEntry (Buyer company) error: ', err);
            return of(options);
          }),
          finalize(() => {
            this.value += 10;
            this.exportHeader = 'Експорт -> дані компанії покупця';
          })
        )),

        // ── Stage 6: seller person ──────────────────────────────────────────────
        concatMap(options => this.cliApi.getPerListEntry(options.opDocument.DOCLCOPERID).pipe(
          map(data => {
            if (data && data.length > 0) {
              const plr = data[0];
              this._sellerPersonData = plr;
              this.documentExport.Seller = {
                ...this.documentExport.Seller,
                Email: plr.PERSONEMAIL || '',
                FullName: plr.personFullName || '',
                Phone: (plr.personWorkPhone || '') + (plr.personMobilePhone || ''),
              };
            }
            return options;
          }),
          catchError(err => {
            environment.production || console.error('*** ExportDocumentComponent getPerListEntry (Seller person) error: ', err);
            return of(options);
          }),
          finalize(() => {
            this.value += 10;
            this.exportHeader = 'Експорт -> дані продавця';
          })
        )),

        // ── Stage 6.5: load logo/stamp/sign images ─────────────────────────────
        concatMap(options => {
          const sellerCompany = this._sellerCompanyData;
          const sellerPerson = this._sellerPersonData;

          return forkJoin({
            logo: (this.showLogo && sellerCompany?.CLILOGOID)
              ? this.cliApi.getImageById(sellerCompany.CLILOGOID).pipe(
                  catchError(err => {
                    environment.production || console.debug('*** ExportDocumentComponent logo loading error: ', err);
                    return of(null);
                  })
                )
              : of(null),
            stamp: (this.showStamp && sellerCompany?.CLISTAMPID)
              ? this.cliApi.getImageById(sellerCompany.CLISTAMPID).pipe(
                  catchError(err => {
                    environment.production || console.debug('*** ExportDocumentComponent stamp loading error: ', err);
                    return of(null);
                  })
                )
              : of(null),
            sign: (this.showSign && sellerPerson?.PERSSIGNID)
              ? this.cliApi.getImageById(sellerPerson.PERSSIGNID).pipe(
                  catchError(err => {
                    environment.production || console.debug('*** ExportDocumentComponent sign loading error: ', err);
                    return of(null);
                  })
                )
              : of(null),
          }).pipe(
            map(result => {
              this.documentExport.Seller.LogoImage = result.logo
                ? 'data:' + result.logo.IMAGENOTE + ';base64,' + result.logo.IMAGEDATA
                : '';
              this.documentExport.Seller.StampImage = result.stamp
                ? 'data:' + result.stamp.IMAGENOTE + ';base64,' + result.stamp.IMAGEDATA
                : '';
              this.documentExport.Seller.SignImage = result.sign
                ? 'data:' + result.sign.IMAGENOTE + ';base64,' + result.sign.IMAGEDATA
                : '';
              return options;
            }),
            catchError(err => {
              environment.production || console.error('*** ExportDocumentComponent image stage error: ', err);
              return of(options);
            }),
            finalize(() => {
              this.value += 10;
              this.exportHeader = 'Експорт -> зображення документа';
            })
          );
        }),

        // ── Stage 7: buyer person ───────────────────────────────────────────────
        concatMap(options => this.cliApi.getPerListEntry(options.opDocument.DOCPCOPERID).pipe(
          map(data => {
            if (data && data.length > 0) {
              const plr = data[0];
              this.documentExport.Buyer = {
                ...this.documentExport.Buyer,
                Email: plr.PERSONEMAIL || '',
                FullName: plr.personFullName || '',
                Phone: (plr.personWorkPhone || '') + (plr.personMobilePhone || ''),
              };
            }
            return options;
          }),
          catchError(err => {
            environment.production || console.error('*** ExportDocumentComponent getPerListEntry (Buyer person) error: ', err);
            return of(options);
          }),
          finalize(() => {
            this.value += 10;
            this.exportHeader = 'Експорт -> дані покупця';
          })
        )),

        // ── Stage 8: owner / responsible manager ────────────────────────────────
        concatMap(options => this.cliApi.getPerListEntry(options.opDocument.DOCMANPERID).pipe(
          map(data => {
            if (data && data.length > 0) {
              const plr = data[0];
              this.documentExport.Owner = {
                ...this.documentExport.Owner,
                Email: plr.PERSONEMAIL || '',
                FullName: plr.personSaveAs || '',
                Phone: plr.personMobilePhone || '',
              };
            }
            return options;
          }),
          catchError(err => {
            environment.production || console.error('*** ExportDocumentComponent getPerListEntry (Owner) error: ', err);
            return of(options);
          }),
          finalize(() => {
            this.value += 10;
            this.exportHeader = 'Експорт -> відповідальний менеджер';
          })
        )),

        // ── Stage 9: document type metadata ────────────────────────────────────
        concatMap(options => this.docApi.getDocTypeById(options.opDocument.DOCTYPE).pipe(
          map(data => {
            if (data && data.length > 0) {
              // Reserved for PrefaceStatement / FooterStatement when API provides them.
              this.documentExport.Conditions = { ...this.documentExport.Conditions };
            }
            return options;
          }),
          catchError(err => {
            environment.production || console.error('*** ExportDocumentComponent getDocTypeById error: ', err);
            return of(options);
          }),
          finalize(() => {
            this.value += 10;
            this.exportHeader = 'Експорт -> тип документа';
          })
        )),

        // ── Stage 10: map line items ────────────────────────────────────────────
        concatMap(options => of(options.opDocument.items).pipe(
          map(items => {
            if (items && items.length > 0) {
              this.documentExport.Items = items
                .filter(di => options.opCountEmpty || di.DOCITEMQTY !== 0)
                .map(di => ({
                  ItemCode: di.PRODCODE,
                  ItemName: di.DOCITEMPRODNAME,
                  ItemTariffCode: di.PRODCUSTCODE || '',
                  ItemNote: di.DOCITEMTEXT1 || '',
                  ItemText: di.DOCITEMTEXT2 || '',
                  ItemPrice: di.DOCITEMPRICE,
                  ItemQnty: di.DOCITEMQTY,
                  ItemSum: di.DOCITEMSUM,
                  ItemVSum: di.DOCITEMVSUM,
                  ItemSumWoVAT: di.DOCITEMSUMWOVAT,
                  ItemVAT: di.PRODVAT,
                  ItemPriceWoVAT: di.DOCITEMPRICEWOVAT,
                  ItemVPrice: di.DOCITEMVPRICE,
                  ItemRetailVPrice: di.DOCITEMRETVPRICE,
                  ItemRetailPrice: di.DOCITEMRETPRICE,
                  ItemDiscount: di.DISCOUNT,
                  ItemContent: di.PRODKIND === 1 ? [] : null,
                  ItemVendorPrice: 0,
                  ItemInvoicePrice: 0,
                  ItemInvoiceSum: 0,
                  ItemIncomePrice: 0,
                  ItemIncomeSum: 0,
                  ItemProdWeight: 0,
                  ItemProdWeightSum: 0,
                }));
              this.value += items.length;
            }
            return options;
          }),
          catchError(err => {
            environment.production || console.error('*** ExportDocumentComponent document items mapping error: ', err);
            return of(options);
          }),
          finalize(() => {
            this.value += 10;
            this.exportHeader = 'Експорт -> таблична частина документа';
          })
        )),

        // ── Stage 11: composite product parts ──────────────────────────────────
        concatMap(options => from(options.opDocument.items as DocItems[]).pipe(
          filter(di => di.PRODKIND === 1 && (options.opCountEmpty || !!(di.DOCITEMQTY && di.DOCITEMQTY !== 0))),
          concatMap(di => this.prodApi.getProdParts(di.PRODID).pipe(
            map(data => {
              const item = this.documentExport.Items.find(e => e.ItemCode === di.PRODCODE);
              if (item) {
                item.ItemContent = data.map(p => ({
                  ItemStr: p.PARTNOTE,
                  ItemCode: p.PARTCODE,
                  ItemName: p.PARTNAME,
                  ItemQnty: p.QUANTITY,
                  ItemPrice: p.PARTPRICE,
                  ItemVPrice: 0,
                }));
                this.value += 1;
              }
            }),
            catchError(err => {
              environment.production || console.error('*** ExportDocumentComponent getProdParts error for product ' + di.PRODCODE + ': ', err);
              return EMPTY;
            }),
            finalize(() => {
              this.value += 5;
              this.exportHeader = 'Експорт -> склад: ' + di.PRODCODE;
            })
          )),
          catchError(err => {
            environment.production || console.error('*** ExportDocumentComponent complex products pipe error: ', err);
            return of(options);
          }),
          finalize(() => {
            this.value += 10;
            this.exportHeader = 'Експорт -> завершено';
            this.notReady = false;
          })
        )),

        // ── Safety-net: catches anything that escaped all inner catchErrors ──────
        // Returns EMPTY so this emission ends cleanly; the Subject stays alive.
        catchError(err => {
          environment.production || console.error('*** ExportDocumentComponent pipeline safety-net error: ', err);
          this.notReady = false;
          this.messageService.add({ severity: 'error', summary: 'Помилка формування експорту документа' });
          return EMPTY;
        })

      )) // ← closes switchMap

    ).subscribe({
      next: () => {
        environment.production || console.log('*** ExportDocumentComponent pipeline next (stage complete)');
      },
      error: err => {
        // True last-resort — should never fire because safety-net returns EMPTY.
        environment.production || console.error('*** ExportDocumentComponent pipeline fatal error (should not reach here): ', err);
        this.notReady = false;
        this.messageService.add({ severity: 'error', summary: 'Критична помилка експорту' });
      },
      complete: () => {
        environment.production || console.log('*** ExportDocumentComponent pipeline complete');
      }
    });
  }

  /**
   * @deprecated Legacy sequential export pipeline kept for reference only.
   * Replaced by the Subject-based pipeline wired in the constructor.
   * Uses `concat` to execute API calls one-by-one without `catchError`,
   * meaning a single failure terminates the whole sequence.
   * Unsubscribes any active `_exportQuery` before creating a new one.
   */
  dumbProcedure() {
    console.log('*** ExportDocumentComponent dumbProcedure');

    // Old export document building implementation

    if (this._exportQuery) { this._exportQuery.unsubscribe(); }

    this._exportQuery =
      concat(

        /*
         * Get Currency Name
         */
        this.restapi.getCurrencyInfoByID(this.globalsettingsservice.getMainCurrencyId()).pipe(
          map((data: Currency[]) => {
            console.log('*** ExportDocumentComponent in exportDocument: (getCurrencyInfoByID)===> ' + JSON.stringify(data));

            let curr: Currency = data[0];

            this.documentExport = {
              ...this.documentExport,
              ... {
                Header: {
                  ...this.documentExport.Header,

                  MainCurrencyName: curr.CURNAME
                }
              }
            };

          })),

        this.restapi.getCurrencyInfoByID(this.document.DOCCURRID).pipe(
          map((data: Currency[]) => {
            console.log('*** ExportDocumentComponent in exportDocument: (getCurrencyInfoByID)===> ' + JSON.stringify(data));

            let curr: Currency = data[0];

            this.documentExport = {
              ...this.documentExport,
              ... {
                Header: {
                  ...this.documentExport.Header,

                  CurrencyName: curr.CURNAME
                }
              }
            };

          })),

        this.restapi.getNumberByWords(this.document.DOCSUM).pipe(map((data: any) => {
          console.log('*** ExportDocumentComponent in exportDocument: (getNumberByWords)===> ' + JSON.stringify(data));

          let res = data.result;

          this.documentExport = {
            ...this.documentExport,
            ... {
              Summary: {
                ...this.documentExport.Summary,

                TotalByWords: res.convertedInteger + ' ' + res.integerCurrency + ' ' + res.fractional + ' ' + res.fractionalCurrency
              }
            }
          };

        })),
        /*
         * Document Seller Sign and Stamp
         */
        //this.cliApi.getPersonImagesInfo(this.document.DOCLCOPERID).pipe(map((data) => {
        //  console.log('*** ExportDocumentComponent in getPersonImagesInfo: ===> ');
        //  let images = data[0];

        //  let sign = new ArrayBuffer (images.PERSONSIGN);
        //  //  console.log('*** ExportDocumentComponent onConfirm (ok): ' + res.byteLength);
        //  let stamp = new ArrayBuffer(images.COMPANYSTAMP);
        //  console.log('*** ExportDocumentComponent in getPersonImagesInfo: ===> PERSONSIGN ' + sign.byteLength);
        //  console.log('*** ExportDocumentComponent in getPersonImagesInfo: ===> COMPANYSTAMP ' + stamp.byteLength);
        //})),

        /*
         * Document Seller Company Info
         */
        this.cliApi.getCliListEntry(this.document.DOCLCOCLIID).pipe(map((data: CliListEntry[]) => {

          let clr: CliListEntry = data[0];
          console.log('*** ExportDocumentComponent in exportDocumentSellerCompany: ===> ' + JSON.stringify(data));

          this.documentExport = {
            ...this.documentExport,
            ... {
              Seller: {
                ... this.documentExport.Seller,

                CompanyName: clr.CLIENTSNAME,
                CompanyId: clr.CLIENTREGCODE || '',
                VATID: clr.CLIENTVATREGCODE || '',
                VATIDDoc: clr.CLIENTVATREGNUM || '',
                Account: clr.CLIENTACCOUNT || '',
                Bank: clr.CLIENTBANK || '',
                Account2: clr.CLIENTBANK2ACC || '',
                Address:
                  (((clr.CLIENTZIP || '') != '') ? clr.CLIENTZIP + ', ' : '') +
                  (((clr.CLIENTCOUNTRY || '') != '') ? clr.CLIENTCOUNTRY + ', ' : '') +
                  (((clr.CLIENTCITY || '') != '') ? clr.CLIENTCITY + ', ' : '') +
                  (((clr.CLIENTADDR1 || '') != '') ? clr.CLIENTADDR1 + ', ' : '') +
                  (((clr.CLIENTADR2 || '') != '') ? clr.CLIENTADR2 + ', ' : '') +
                  ''
              },
            }
          };
        })),
        /*
         * Document Buyer Company Info
         */
        this.cliApi.getCliListEntry(this.document.DOCPCOCLIID).pipe(map((data: CliListEntry[]) => {

          let clr: CliListEntry = data[0];

          console.log('*** ExportDocumentComponent in exportDocumentBuyerCompany: ===> ' + JSON.stringify(data));

          this.documentExport = {
            ...this.documentExport,
            ... {
              Buyer: {
                ... this.documentExport.Buyer,

                CompanyName: clr.CLIENTSNAME,
                CompanyId: clr.CLIENTREGCODE || '',
                VATID: clr.CLIENTVATREGCODE || '',
                VATIDDoc: clr.CLIENTVATREGNUM || '',
                Account: clr.CLIENTBANK2ACC || '',
                Account2: clr.CLIENTACCOUNT || '',
                Bank: clr.CLIENTBANK || '',
                Address:
                  (((clr.CLIENTZIP || '') != '') ? clr.CLIENTZIP + ', ' : '') +
                  (((clr.CLIENTCOUNTRY || '') != '') ? clr.CLIENTCOUNTRY + ', ' : '') +
                  (((clr.CLIENTCITY || '') != '') ? clr.CLIENTCITY + ', ' : '') +
                  (((clr.CLIENTADDR1 || '') != '') ? clr.CLIENTADDR1 + ', ' : '') +
                  (((clr.CLIENTADR2 || '') != '') ? clr.CLIENTADR2 + ', ' : '') +
                  ''
              },
            }
          };
        })),
        /*
         * Document Seller Person Info
         */
        this.cliApi.getPerListEntry(this.document.DOCLCOPERID).pipe(map((data: PerListEntry[]) => {

          let plr: PerListEntry = data[0];
          console.log('*** ExportDocumentComponent in exportDocumentSellerPerson: ===> ' + JSON.stringify(data));

          this.documentExport = {
            ...this.documentExport,
            ... {
              Seller: {
                ...this.documentExport.Seller,

                Email: (plr.PERSONEMAIL || ''),
                FullName: (plr.personFullName || ''),
                Phone: (plr.personWorkPhone || '') + (plr.personMobilePhone || '')
              },
            }
          };
        })),
        /*
         * Document Buyer Person Info
         */
        this.cliApi.getPerListEntry(this.document.DOCPCOPERID).pipe(map((data: PerListEntry[]) => {

          let plr: PerListEntry = data[0];
          console.log('*** ExportDocumentComponent in exportDocumentBuyerPerson: ===> ' + JSON.stringify(data));

          this.documentExport = {
            ...this.documentExport,
            ... {
              Buyer: {
                ...this.documentExport.Buyer,

                Email: (plr.PERSONEMAIL || ''),
                FullName: (plr.personFullName || ''),
                Phone: (plr.personWorkPhone || '') + (plr.personMobilePhone || '')
              },
            }
          };
        })),
        /*
         * Document Owner Info
         */
        this.cliApi.getPerListEntry(this.document.DOCMANPERID).pipe(map((data: PerListEntry[]) => {

          let plr: PerListEntry = data[0];
          console.log('*** ExportDocumentComponent in exportDocumentOwner: ===> ' + JSON.stringify(data));

          this.documentExport = {
            ...this.documentExport,
            ... {
              Owner: {
                ...this.documentExport.Owner,

                Email: plr.PERSONEMAIL,
                FullName: plr.personSaveAs,
                Phone: plr.personMobilePhone
              },
            }
          };
        })),

        this.docApi.getDocTypeById(this.document.DOCTYPE).pipe(map(data => {
          console.log('*** ExportDocumentComponent in get DOCTYPE: ===> ' + JSON.stringify(data));

          let res: DocTypes = data[0];

          this.documentExport = {
            ...this.documentExport,
            ... {

              Conditions: {
                ... this.documentExport.Conditions,

                //PrefaceStatement: res.DOCTEXTBEFORE,
                //FooterStatement: res.DOCTEXTAFTER,

              }
            }
          };

        })),

        /*
         * NOTE! NOTE! NOTE!
         * 
         * Here we will get the items, already prepared in the document's component
         *
         * Just because it is much easy and we will avoid duplicate items processing
         *
         * However product's items we will pull from database, again - just becuase it is more easy
         * 
         */
        of(this.document.items).pipe(

          map(items => {

            this.documentExport.Items =
              items
                .filter(e => (this.countEmpty || e.DOCITEMQTY))

                .map(di => ({
                  ItemCode: di.PRODCODE,
                  ItemName: di.DOCITEMPRODNAME,
                  ItemTariffCode: di.PRODCUSTCODE || '',
                  ItemNote: di.DOCITEMTEXT1 || '',
                  ItemText: di.DOCITEMTEXT2 || '',
                  ItemPrice: di.DOCITEMPRICE,
                  ItemQnty: di.DOCITEMQTY,
                  ItemSum: di.DOCITEMSUM,
                  ItemVSum: di.DOCITEMVSUM,
                  ItemSumWoVAT: di.DOCITEMSUMWOVAT,
                  ItemVAT: di.PRODVAT,
                  ItemPriceWoVAT: di.DOCITEMPRICEWOVAT,
                  ItemVPrice: di.DOCITEMVPRICE,
                  ItemRetailVPrice: di.DOCITEMRETVPRICE,
                  ItemRetailPrice: di.DOCITEMRETPRICE,
                  ItemDiscount: di.DISCOUNT,
                  ItemContent: (di.PRODKIND == 1) ? [] : null,

                  ItemVendorPrice: 0,

                  ItemInvoicePrice: 0,
                  ItemInvoiceSum: 0,

                  ItemIncomePrice: 0,
                  ItemIncomeSum: 0,

                  ItemProdWeight: 0,
                  ItemProdWeightSum: 0,

                }));

            this.value += items.length;

            environment.production || console.table(this.documentExport.Items);
          }),
          tap(dis => environment.production || console.table(dis)),
        ),

        /*
         * try to process complex product separately from other tasks
         *
         * just for beauty of code (very very unclear goal)
         */
        from(this.document.items).pipe(
          filter(di => (di.PRODKIND == 1) && (this.countEmpty || (di.DOCITEMQTY && (di.DOCITEMQTY != 0)))),

          concatMap(di => this.prodApi.getProdParts(di.PRODID).pipe(

            map(data => {
              let i = this.documentExport.Items.find(e => e.ItemCode == di.PRODCODE);
              if (i) {
                i.ItemContent =
                  data.map(p => ({
                    ItemStr: p.PARTNOTE,
                    ItemCode: p.PARTCODE,
                    ItemName: p.PARTNAME,
                    ItemQnty: p.QUANTITY,
                    ItemPrice: p.PARTPRICE,
                    ItemVPrice: 0,
                  }));
              }
            }))),

          finalize(() => {
            console.log('*** ExportDocumentComponent document Export items with parts processed');
            this.notReady = false;
          }
          )
        )
      )
        .subscribe(() => {
          //console.log('*** ExportDocumentComponent document Export forming: ' + JSON.stringify(this.documentExport));

          this.value += 10;

        });
  }

  /**
   * Angular lifecycle hook — cleans up all active subscriptions to prevent
   * memory leaks when the component is removed from the DOM.
   */
  ngOnDestroy(): void {
    if (this._documentQuery) { this._documentQuery.unsubscribe(); }
    if (this._exportQuery) { this._exportQuery.unsubscribe(); }
    if (this.reportListQuery) { this.reportListQuery.unsubscribe(); }
  }

  /**
   * Angular lifecycle hook — called once after the component is initialised.
   * Currently used only for diagnostic logging.
   */
  ngOnInit(): void {
    console.log('*** ExportDocumentComponent ngOnInit: ');
  }

  /**
   * Angular lifecycle hook — reacts to changes in bound `@Input()` properties.
   * Handles:
   * - `documentId` — triggers {@link loadDocumentById} to fetch the document from the API.
   * - `display`    — reserved for future initialisation logic on dialog open.
   *
   * @param changes - Map of changed input property names to their {@link SimpleChange} values.
   */
  ngOnChanges(changes: SimpleChanges) {
    for (let propName in changes) {
      let change = changes[propName];
      console.log('*** ExportDocumentComponent ngOnChanges (actual): ' + propName + ' ' + JSON.stringify(change.currentValue));

      switch (propName) {
        case 'documentId':
          this.loadDocumentById(change.currentValue);
          break;

        case 'display':
          if (change.currentValue) {
          }
          break;
      }
    }
  }

  /**
   * Loads a document by its numeric ID and populates {@link document} with
   * the result, including its display name and document-type header/footer text.
   *
   * Pipeline stages (all with `catchError` so failures are non-fatal):
   * 1. Fetch the document record — filters out empty results.
   * 2. Resolve the human-readable document name string.
   * 3. Resolve the document-type metadata to obtain `HeaderStatement` and
   *    `FooterStatement` text blocks.
   *
   * On outer failure a toast error is shown via {@link messageService}.
   *
   * @param _docId - The numeric primary key of the document to load (`DOCID`).
   */
  loadDocumentById(_docId: number) {
    this._documentQuery = this.docApi.getDocumentById(_docId).pipe(

      filter(doc => doc && doc.length > 0),

      map(data => {
        this.document = data[0];
        return this.document;
      }),

      concatMap(doc => this.docApi.getDocumentNameById(doc.DOCID).pipe(
        map(data => {
          doc.DOCNAMESTR = (data[0] || { 'DOCNAMESTR': 'Empty document' }).DOCNAMESTR;
          return doc;
        }),
        catchError(err => {
          environment.production || console.error('*** ExportDocumentComponent getDocumentNameById error: ', err);
          return of(doc);
        })
      )),

      concatMap(doc => this.docApi.getDocTypeById(doc.DOCTYPE).pipe(
        filter(data => data && data.length > 0),

        map(data => {
          let res: DocTypes = data[0];

          this.document.HeaderStatement = res.DOCTEXTBEFORE;
          this.document.FooterStatement = res.DOCTEXTAFTER;
        }),
        catchError(err => {
          environment.production || console.error('*** ExportDocumentComponent getDocTypeById (loadDocumentById) error: ', err);
          return EMPTY;
        })
      )),

      catchError(err => {
        environment.production || console.error('*** ExportDocumentComponent loadDocumentById error: ', err);
        this.messageService.add({ severity: 'error', summary: 'Помилка завантаження документа' });
        return EMPTY;
      })

    ).subscribe();
  }

  /**
   * Called by the PrimeNG dialog `(onShow)` event binding when the dialog becomes visible.
   * Unsubscribes any previous report-list query, then fetches the available print forms
   * for the current document type and pre-selects the first matching form.
   * Filters forms to those either unassigned (`LCOCLIID = 0`) or belonging to the
   * seller company (`DOCLCOCLIID`). On success calls {@link onExport} to begin
   * assembling the export payload.
   * On failure a toast error is shown via {@link messageService}.
   */
  onShowDialog() {
    console.log('*** ExportDocumentComponent onShow: ');
    this.notReady = true;

    if (this.document) {
      if (this.reportListQuery) { this.reportListQuery.unsubscribe(); }

      this.reportListQuery =
        this.restapi.getPrintReportsByDocType(this.document.DOCTYPE)
          .pipe(

            // Select all available print forms for given document type and pre-select first in row

            filter(list => list && (list.length > 0)),

            map(list => {
              this.reportList = list.filter(r => (((r.LCOCLIID ?? 0) == 0) || (r.LCOCLIID == this.document.DOCLCOCLIID)));

              if ((this.reportList || []).length > 0) {
                this.selectedReport = this.reportList[0];
              }
              this.onExport();
            }),

            catchError(err => {
              environment.production || console.error('*** ExportDocumentComponent getPrintReportsByDocType error: ', err);
              this.messageService.add({ severity: 'error', summary: 'Помилка завантаження форм друку' });
              return EMPTY;
            }),

            finalize(() => {
              environment.production || console.log('*** ExportDocumentComponent getPrintReportsByDocType finalize');
            })
          )
          .subscribe();
    }
  }

  /**
   * Called by the PrimeNG dialog `(onHide)` event binding when the dialog is closed.
   * Resets the progress bar value and emits `false` on {@link displayChange} to keep
   * the parent's two-way `[(display)]` binding in sync.
   */
  onHideDialog() {
    console.log('*** ExportDocumentComponent onHide');
    this.value = 0;     // Give more time for progress bar indicator
    this.displayChange.emit(false);
  }

  /**
   * Called when the user clicks a dialog action button.
   * Emits the {@link done} event and closes the dialog regardless of the button chosen.
   * When `text === 'ok'`, assembles the full report request body from the current
   * {@link documentExport} payload and the selected print-form settings, then POSTs it
   * to the jsreport server via {@link ReportApiService.getInvoiceForm}.
   * The returned binary DOCX is saved locally using `file-saver`.
   *
   * ### Report server URL resolution
   * The report server address is stored as a JSON string in global settings:
   * ```
   * { "report1": "https://HOST:5589", "apiUrl": "/api/report/" }
   * ```
   * `stringInject` is used to interpolate the `REPORTSERVER` template string
   * from the selected print form with the parsed server data object.
   *
   * @param text - Action identifier; `'ok'` triggers document generation,
   *               any other value dismisses the dialog without exporting.
   */
  onConfirm(text: string) {
    console.log('*** ExportDocumentComponent onConfirm: ' + text);
    this.done.emit(true);
    this.display = false;

    let docx = {};

    if (text == 'ok') {

      // This is spare template name, in case no report selected
      // By fact there is no such template on the report server

      let templateName = '/webyto/invoice/general-document';

      //switch (this.document.DOCTYPE) {
      //  case 1:
      //    templateName = '/webyto/invoice/invoice-word'; //QHWGXsA

      //    let printFormShortId = this.printFormAssets.find(e => e.CLIID == this.document.DOCLCOCLIID).INVOICEID;

      //    docx = {

      //        "templateAssetShortid": this.useDevForm ? "B20PotT" : printFormShortId
      //    };
      //    break;

      //  case 10:
      //    templateName = '/webyto/invoice/purchase-order';
      //    docx = {
      //        "templateAssetShortid": "UC_Z839",
      //    };
      //    break;

      //  case 12:
      //    templateName = '/webyto/invoice/proforma-word';
      //    docx = {
      //        "templateAssetShortid": this.showDiscount ? "UC_Z839" : "Lq7o2nL",
      //    };
      //    break;
      //}

      let data = {
        ... this.documentExport,

        "ExportOptions": {
          ShowDiscount: this.showDiscount,
          ShowCurrency: this.showCurrency,
          ShowItem: this.showItem,
          ShowItemTable: this.showItemTable,
          showLogo: this.showLogo,
          showStamp: this.showStamp,
          showSign: this.showSign
        }

      };

      /*
       * Seems here we need some tricks to parse that staff
       * 
       * 1) string of report server(s) to enumerate all given
       *
       * "reportSrvAddress":
       *    {"report1":"https://REPORT2.DISTI.PRO:5589", "report2":"https://REPORT2.DISTI.PRO:5489", "apiUrl": "/api/report/"}
       * 
       * JSON.parse() => report1, report2, apiUrl
       *
       * stringInject() => "https://REPORT2.DISTI.PRO:5589/api/report/"
       *
       * const templateFn = expression => `string text ${expression} string text`;
       * const reportServerURL = (serversJson, serverUrl) => { return `${serverUrl}`; }
       * const paramSet = { a1: "here a1", a2: "but a2", a3: "this is a3" };
       * const resultSet = stringInject("Hey! {a1}, and {a2}, also {a3}. test {a4}", paramSet);
       */

      console.log(this.globalsettingsservice.getReportServerUrl());

      const reportServerData = JSON.parse(this.globalsettingsservice.getReportServerUrl());

      const reportServerUrl = stringInject(this.selectedReport.REPORTSERVER, reportServerData);

      console.log(reportServerUrl);

      let template = this.selectedReport
        ? {
          ...JSON.parse(this.selectedReport.REPORTSRVOPT),
          "reportServerURI": reportServerUrl,

          // Old authentication method before we started to use report server with keycloak auth
          // "AuthKeyStr": ("secret" in reportServerData) ? reportServerData.secret : "YWRtaW46anNBYnJ1cHQ0IQ=="
        }
        : {
          "template": {
            "name": templateName,
            "recipe": "docx",
            "engine": "handlebars",
            "docx": {
              ...docx
            }
          }
        };

      let body = {

        ...template,
        "data": data,
        "options": {
          "reportName": this.documentExport.Header?.DocumentName,
          "reports": {
            "save": true,
            "blobName": this.documentExport.DocumentId?.toString(),
          }
        }
      };

      // Share final body for debug purposes

      environment.production || console.log('*** ExportDocumentComponent document Export onConfirm just before the POST: ' + JSON.stringify(body, null, 2));

      this.reportApi.getInvoiceForm(body)
        .pipe(
          map(data => {

            let fileName: string = this.document.DOCNUMBEREX + '.docx';
            saveAs(data, fileName);
            this.messageService.add({ severity: 'info', summary: 'Документ сохранен как: ' + fileName });
          }),
          catchError(err => {
            environment.production || console.error('*** ExportDocumentComponent getInvoiceForm error: ', err);
            this.messageService.add({ severity: 'error', summary: 'Помилка отримання форми документа' });
            return EMPTY;
          })
        )
        .subscribe();
    }
  }

  /*
    export class DocumentExport {
      DocumentId?: number;
  
      Owner?: {
        FullNameLabel?: string;
        PhoneLabel?: string;
        EmailLabel?: string;
  
        FullName?: string;
        Phone?: string;
        Email?: string;
      };
  
      Header?: {
  
        DocumentNumberLabel?: string;
        DocumentNameLabel?: string;
        DateLabel?: string;
        ValidToLabel?: string;
        ExpiredOnLabel?: string;
  
        CurrencyNameLabel?: string;
        CurrencyRateLabel?: string;
  
        DocumentNumber?: string;
        DocumentName?: string;
        Date?: Date;
        ValidTo?: Date;
        ExpiredOn?: Date;
  
        CurrencyName?: string;
        CurrencyRate?: number;
  
      };
  
      Seller?: {
        CompanyNameLabel?: string;
        CompanyIdLabel?: string;
        VATIDLabel?: string;
        AccountLabel?: string;
        BankLabel?: string;
        AddressLabel?: string;
        Account2Label?: string;
  
        FullNameLabel?: string;
        PhoneLabel?: string;
        EmailLabel?: string;
  
        CompanyName?: string;
        CompanyId?: string;
        VATID?: string;
        Account?: string;
        Bank?: string;
        Address?: string;
        Account2?: string;
  
        FullName?: string;
        Phone?: string;
        Email?: string;
      };
  
      Buyer?: {
        CompanyNameLabel?: string;
        CompanyIdLabel?: string;
        VATIDLabel?: string;
        AccountLabel?: string;
        BankLabel?: string;
        AddressLabel?: string;
        Account2Label?: string;
  
        FullNameLabel?: string;
        PhoneLabel?: string;
        EmailLabel?: string;
  
        CompanyName?: string;
        CompanyId?: string;
        VATID?: string;
        Account?: string;
        Account2?: string;
        Bank?: string;
        Address?: string;
  
        FullName?: string;
        Phone?: string;
        Email?: string;
      };
  
      ItemsLabel?: {
        ItemCodeLabel?: string;
        ItemNameLabel?: string;
        ItemTariffCodeLabel?: string;
        ItemNoteLabel?: string;
        ItemTextLabel?: string;
  
        ItemQntyLabel?: string;
        ItemPriceLabel?: string;
        ItemSumLabel?: string;
        ItemVATLabel?: string;
  
        ItemSumWoVATLabel?: string;
        ItemPriceWoVATLabel?: string;
        ItemVPriceLabel?: string;
  
        ItemContentLabel?: {
          ItemStrLabel?: string;
          ItemCodeLabel?: string;
          ItemNameLabel?: string;
          ItemQntyLabel?: string;
          ItemPriceLabel?: string;
          ItemVPriceLabel?: string;
        };
      };
  
  
      Items?: {
        ItemCode?: string;
        ItemName?: string;
        ItemTariffCode?: string;
        ItemNote?: string;
        ItemText?: string;
  
        ItemQnty?: number;
        ItemPrice?: number;
        ItemSum?: number;
        ItemVAT?: number;
  
        ItemSumWoVAT?: number;
        ItemPriceWoVAT?: number;
        ItemVPrice?: number;
  
        ItemContent?: {
          ItemStr?: string;
          ItemCode?: string;
          ItemName?: string;
          ItemQnty?: number;
          ItemPrice?: number;
          ItemVPrice?: number;
        }[];
  
      }[];
  
      Summary?: {
  
        TotalSumLabel?: string;
        TotalSumWithOutVATLabel?: string;
        TotalSumVATLabel?: string;
        TotalItemsLabel?: string;
        TotalQuantityLabel?: string;
  
        TotalByWordsLabel?: string;
  
        TotalSum?: number;
        TotalSumWithOutVAT?: number;
        TotalSumVAT?: number;
        TotalItems?: number;
        TotalQuantity?: number;
  
        TotalByWords?: string;
      };
  
      Conditions?: {
        DeliveryConditionsLabel?: string;
        PaymentConditionsLabel?: string;
        DocumentNoteLabel?: string;
  
        PrefaceStatementLabel?: string;
        FooterStatementLabel?: string;
  
        DeliveryConditions?: string;
        PaymentConditions?: string;
        DocumentNote?: string;
  
        PrefaceStatement?: string;
        FooterStatement?: string;
      };
    };
  
  */

  /**
   * Pushes a new {@link BuildOptions} snapshot onto the {@link buildExportData}
   * Subject to trigger a full rebuild of the {@link documentExport} payload.
   * Called automatically by {@link onShowDialog} and by option-change handlers
   * ({@link onShowItemChange}, {@link onCountEmptyChange}).
   */
  onExport() {
    console.log('*** ExportDocumentComponent onExport');

    this.buildExportData.next({
      opDocument: this.document,
      opDocumentSpecific: this.documentSpecific,
      opCountEmpty: this.countEmpty,
      opShowCurrency: this.showCurrency,
      opShowDiscount: this.showDiscount,
      opShowItem: this.showItem,
      opShowItemTable: this.showItemTable,
    });
  }

  /**
   * Handles changes to the "show item table" toggle control.
   * Updates {@link showItemTable} from the checkbox event and triggers
   * a full export payload rebuild via {@link onExport}.
   *
   * @param event - PrimeNG checkbox change event; `event.checked` carries the new boolean value.
   */
  onShowItemChange(event) {
    console.log('*** ExportDocumentComponent onShowItemChange' + JSON.stringify(event));

    this.showItemTable = event.checked;
    this.onExport();
  }

  /**
   * Handles changes to the "include zero-quantity items" toggle control.
   * Updates {@link countEmpty} from the checkbox event and triggers
   * a full export payload rebuild via {@link onExport}.
   *
   * @param event - PrimeNG checkbox change event; `event.checked` carries the new boolean value.
   */
  onCountEmptyChange(event) {
    console.log('*** ExportDocumentComponent onCountEmptyChange' + JSON.stringify(event));

    this.countEmpty = event.checked;
    this.onExport();
  }
}
